// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';
import { type Country } from '@tectonics/prs/country';
import { isLocalLatamTenantCode } from '@tectonics/commons/tenant';
import { useControllerContext } from '@tectonics/controller/context';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import { Link } from '../../../atoms';
import { translate } from '@tectonics/translate';

export function Terms(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);
  const { localTenancy } = useControllerContext();

  switch (country) {
    case 'br':
      return (isLocalLatamTenantCode(localTenancy) &&
        <BrazilianTermsForLocalLatam />
      );

    case 'co':
      return <ColombianTerms />;

    case 'ar':
    case 'bo':
    case 'cl':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'uy':
    case 'cr':
    case 'py':
    case 'pa':
    case 'sv':
    case 'gt':
    case 'do':
    case 'jm':
      return <LatamTerms country={country} />;

    case 'eg':
      return <EgyptTerms />;

    case 'za':
    case 'ke':
    case 'ng':
    case 'tz':
    case 'ug':
    case 'zm':
    case 'gh':
      return <AfricanTerms />;

    case 'in':
      return <IndianTerms />;

    default:
      throw new Error(`No message found for Terms accept in country ${country}`);
  }
}

function BrazilianTermsForLocalLatam(): Node {
  return (
    <Paragraph>
      Ao continuar você aceita os
      {'\u00A0'}
      <Link href="https://www.ebanx.com/br/termos" target="_blank" rel="noopener noreferrer">
        termos de uso
      </Link>
      {'\u00A0'}
      do EBANX.
    </Paragraph>
  );
}

function ColombianTerms(): Node {

  return (
    <Paragraph>
      Al continuar, estarás aceptando la
      {'\u00A0'}
      <Link href="https://www.ebanx.com/co/politica-de-privacidad/" target="_blank" rel="noopener noreferrer">
        política de privacidad
      </Link>
      {'\u00A0'}
      y los
      {'\u00A0'}
      <Link href="https://www.ebanx.com/co/condiciones/" target="_blank" rel="noopener noreferrer">
        términos y condiciones
      </Link>
      {'\u00A0'}
      de EBANX.
    </Paragraph>
  );
}

function AfricanTerms(): Node {

  return (
    <Paragraph>
      By paying you agree to the
      {'\u00A0'}
      <Link href="https://business.ebanx.com/en/legal/customers/africa/general-terms" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 'bold' }}>
        terms of service
      </Link>
      {'\u00A0'}
      of EBANX.
    </Paragraph>
  );
}

function LatamTerms({ country }: LatamTermsProps): Node {
  return (
    <Paragraph>
      Al continuar, estarás aceptando los
      {'\u00A0'}
      <Link href={`https://www.ebanx.com/${country}/condiciones`} target="_blank" rel="noopener noreferrer">
        términos y condiciones
      </Link>
      {'\u00A0'}
      de EBANX.
    </Paragraph>
  );
}

function EgyptTerms(): Node {

  return (
    <Paragraph>
      {translate('volcanes.termsLabel')}
      <Link href="https://www.ebanx.com/en/legal/customers/general-terms-and-conditions-egypt/" target="_blank" rel="noopener noreferrer">
        {translate('volcanes.termsReferenceLink')}
      </Link>
      &nbsp;
      {translate('volcanes.termsEbanxLabel')}
    </Paragraph>
  );
}

function IndianTerms(): Node {

  return (
    <Paragraph>
      {translate('volcanes.termsLabel')}
      <Link href="https://www.ebanx.com/en/legal/customers/general-terms-and-conditions-india/" target="_blank" rel="noopener noreferrer">
        {translate('volcanes.termsReferenceLink')}
      </Link>
      {translate('volcanes.termsEbanxLabel')}
    </Paragraph>
  );
}

type LatamTermsProps = {|
  country: Country,
|};

const Paragraph = styled.p`
  font-size: .9em;
  text-align: center;
  color: ${props => props.theme.colors.black};
  `;
